import { Link } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/feature/Seo";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { siteInfo, stats } from "@/mocks/gossipData";

const principles = [
  {
    icon: "ri-filter-3-line",
    title: "先筛选，再呈现",
    desc: "不是把所有信息都堆上来，而是先判断哪些值得看、哪些可以略过，再决定呈现顺序。",
  },
  {
    icon: "ri-layout-masonry-line",
    title: "结构比数量重要",
    desc: "每篇合集都有清晰的分类与看点标注，读者可以在几十秒内判断要不要继续读下去。",
  },
  {
    icon: "ri-links-line",
    title: "标注背景，不做定论",
    desc: "涉及事件背景的内容，我们会说明信息来源与时间线，把判断权交回给读者。",
  },
  {
    icon: "ri-refresh-line",
    title: "有错就改",
    desc: "如果内容出现事实错误或表述不当，我们会在核实后尽快修正，并说明修改内容。",
  },
];

const milestones = [
  { year: "2021", text: "瓜田福利社上线，最初只做综艺与剧集的单篇盘点。" },
  { year: "2022", text: "新增网络热议与时尚生活两个分类，开始尝试「合集」形式。" },
  { year: "2023", text: "上线分类浏览与关键词搜索，内容数量突破三百篇。" },
  { year: "2024", text: "补充音乐现场与体育竞技分类，形成六大门类的内容结构。" },
  { year: "2025", text: "优化移动端阅读体验，每篇合集加入目录与相关推荐。" },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 瓜田福利社",
    description: siteInfo.intro,
    url: `${SITE_URL}/about`,
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteInfo.name,
    url: SITE_URL,
    email: siteInfo.email,
    foundingDate: "2021",
    description: siteInfo.intro,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_URL}/about` },
    ],
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="关于我们｜瓜田福利社的内容理念与整理方式"
        description="了解瓜田福利社的品牌定位、内容原则与发展历程。我们专注娱乐与网络热点的整理与盘点，用结构化的合集帮读者省下四处翻找的时间。"
        keywords="关于瓜田福利社,内容原则,娱乐资讯整理,吃瓜盘点,品牌介绍"
        path="/about"
        ogType="website"
        jsonLd={jsonLd}
      />
      <SiteLayout>
        <section className="w-full bg-background-100 px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "关于我们" }]} />
          <div className="mt-5 flex flex-col gap-4">
            <h1 className="font-heading text-3xl text-foreground-950 md:text-5xl">
              关于我们 · 认真整理每一份瓜
            </h1>
            <p className="max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {siteInfo.name}是一个专注娱乐与网络热点的内容聚合平台。我们相信，吃瓜也可以吃得高效——把信息整理清楚，比抢快更重要。
            </p>
          </div>
        </section>

        <section className="w-full px-4 py-14 md:px-6 md:py-20" aria-label="品牌介绍">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <span className="text-xs font-label uppercase tracking-widest text-primary-600">
                Who We Are
              </span>
              <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">
                我们是谁，在做什么
              </h2>
              <p className="mt-5 text-base leading-loose text-foreground-700">{siteInfo.intro}</p>
              <p className="mt-4 text-base leading-loose text-foreground-700">
                平台成立于 {siteInfo.founded}，由一群长期关注娱乐内容与社交平台趋势的编辑共同维护。
                我们不追求第一时间抢发，而是把同一个话题的多条信息集中到一处，让读者用更少的时间获得更完整的认识。
              </p>
              <div className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-4">
                {stats.map((item) => (
                  <div key={item.label} className="flex flex-col gap-1">
                    <span className="font-heading text-2xl text-primary-600">{item.value}</span>
                    <span className="text-xs text-foreground-500">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="h-64 w-full overflow-hidden rounded-2xl border border-background-200 md:h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20editorial%20desk%20scene%20with%20open%20notebook%2C%20stacked%20magazines%2C%20coffee%20cup%20and%20pen%20on%20cream%20linen%2C%20terracotta%20and%20amber%20color%20palette%2C%20soft%20natural%20window%20light%2C%20calm%20minimal%20composition&width=1000&height=1100&seq=gt-about-2026-b2&orientation=portrait"
                  alt="瓜田福利社编辑工作台，暖色调笔记本与杂志陈列"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 px-4 py-14 md:px-6 md:py-20" aria-label="内容原则">
          <div className="flex flex-col gap-3">
            <span className="text-xs font-label uppercase tracking-widest text-secondary-700">
              Principles
            </span>
            <h2 className="font-heading text-3xl text-foreground-950 md:text-4xl">我们的内容原则</h2>
            <p className="max-w-2xl text-sm text-foreground-600">
              这四条原则决定了我们选择什么内容、以及以什么样的方式呈现给读者。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <div
                key={item.title}
                className="flex gap-4 rounded-xl border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-heading text-lg text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 py-14 md:px-6 md:py-20" aria-label="发展历程">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <span className="text-xs font-label uppercase tracking-widest text-primary-600">
                Journey
              </span>
              <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">
                一路走来的几个节点
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                从单篇盘点到一个完整的内容结构，每一步调整都来自读者的反馈。
              </p>
            </div>
            <ol className="lg:col-span-8">
              {milestones.map((item) => (
                <li key={item.year} className="flex gap-5 pb-8 last:pb-0">
                  <div className="flex flex-col items-center">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-accent-300 bg-accent-50 font-heading text-sm text-accent-700">
                      {item.year}
                    </span>
                    <span className="mt-2 w-px flex-1 bg-background-300" aria-hidden="true" />
                  </div>
                  <p className="pt-2.5 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="w-full px-4 pb-16 md:px-6 md:pb-20" aria-label="联系方式">
          <div className="flex flex-col items-start gap-6 rounded-2xl border border-background-200 bg-secondary-950 p-6 md:flex-row md:items-center md:justify-between md:p-10">
            <div>
              <h2 className="font-heading text-2xl text-background-50 md:text-3xl">和我们聊聊</h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-200">
                无论是选题建议、内容纠错还是合作咨询，都欢迎通过邮件或表单联系我们。
                {siteInfo.replyTime}。
              </p>
              <p className="mt-4 flex items-center gap-2 text-sm text-background-100">
                <i className="ri-mail-line" aria-hidden="true" />
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="transition-colors hover:text-accent-400 cursor-pointer"
                >
                  {siteInfo.email}
                </a>
              </p>
            </div>
            <Link
              to="/contact"
              className="flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-label text-secondary-950 transition-colors hover:bg-accent-400 cursor-pointer"
            >
              <i className="ri-chat-3-line" aria-hidden="true" />
              前往联系页面
            </Link>
          </div>
        </section>
      </SiteLayout>
    </>
  );
}